/**
 * CDN Manager - Utilidad para gestionar recursos a través de CDN
 *
 * Este módulo proporciona funciones para:
 * - Transformar URLs locales a URLs de CDN
 * - Verificar el estado de la CDN
 * - Gestionar fallbacks cuando la CDN no está disponible
 */

interface CDNConfig {
  enabled: boolean;
  baseUrl: string;
  version?: string;
  fallbackToLocal: boolean;
}

// Configuración predeterminada que se cargará desde variables de entorno
const defaultConfig: CDNConfig = {
  enabled: import.meta.env.VITE_CDN_ENABLED === 'true',
  baseUrl: import.meta.env.VITE_CDN_BASE_URL || '',
  version: import.meta.env.VITE_CDN_VERSION || '',
  fallbackToLocal: true,
};

let cdnConfig: CDNConfig = { ...defaultConfig };

/**
 * Inicializa la configuración de la CDN
 */
export function initCDN(config?: Partial<CDNConfig>): void {
  cdnConfig = { ...defaultConfig, ...config };

  if (cdnConfig.enabled && !cdnConfig.baseUrl) {
    console.warn(
      'CDN está habilitada pero no se ha proporcionado una URL base. La CDN se deshabilitará.'
    );
    cdnConfig.enabled = false;
  }
}

/**
 * Transforma una URL local a una URL de CDN
 * @param url URL local del recurso (ej: /images/logo.png)
 * @returns URL transformada para CDN
 */
export function toCDNUrl(url: string): string {
  if (!cdnConfig.enabled || !url) return url;

  // Si la URL ya es absoluta o es una URL de CDN, devolverla sin cambios
  if (url.startsWith('http') || url.startsWith('//')) {
    return url;
  }

  // Eliminar la barra inicial si existe para evitar doble barra en la URL final
  const cleanUrl = url.startsWith('/') ? url.substring(1) : url;

  // Construir la URL de CDN
  let cdnUrl = `${cdnConfig.baseUrl}/${cleanUrl}`;

  // Añadir versión si está configurada (para invalidación de caché)
  if (cdnConfig.version) {
    const separator = url.includes('?') ? '&' : '?';
    cdnUrl += `${separator}v=${cdnConfig.version}`;
  }

  return cdnUrl;
}

/**
 * Verifica si la CDN está activa y accesible
 * @returns Promise que resuelve a true si la CDN está activa y accesible
 */
export async function checkCDNStatus(): Promise<boolean> {
  if (!cdnConfig.enabled) return false;

  try {
    // Intentar cargar un recurso pequeño desde la CDN para verificar su disponibilidad
    const testUrl = `${cdnConfig.baseUrl}/favicon.ico?check=${Date.now()}`;
    const response = await fetch(testUrl, {
      method: 'HEAD',
      cache: 'no-store',
    });

    return response.ok;
  } catch (error) {
    console.error('Error al verificar el estado de la CDN:', error);
    return false;
  }
}

/**
 * Obtiene la configuración actual de la CDN
 */
export function getCDNConfig(): CDNConfig {
  return { ...cdnConfig };
}

/**
 * Habilita o deshabilita la CDN en tiempo de ejecución
 */
export function toggleCDN(enabled: boolean): void {
  cdnConfig.enabled = enabled;
}
